/**
 * React Performance Optimization Utilities
 * 
 * Bu dosya, uygulama genelinde kullanılan performans optimizasyon kalıplarını 
 * ve yardımcı fonksiyonları içerir.
 * 
 * KULLANIM ÖRNEĞİ:
 * import {
 *   useMemoizedColumns,
 *   useMemoizedHandlers,
 *   useMemoizedFormValues
 * } from '../utils/performanceOptimizations'
 */

import { useMemo, useCallback } from 'react'

/**
 * Optimized Redux Selector Patterns
 * 
 * Redux store'dan veri seçerken gereksiz re-render'ları önlemek için
 * selektör fonksiyonları kullanın.
 * 
 * @example
 * const selectUserData = createOptimizedSelector(['user.data', 'user.loading'])
 * const {data, loading} = useSelector(selectUserData)
 */
export const createOptimizedSelector = (fields) => (state) => {
  const result = {}
  fields.forEach(field => {
    const [slice, prop] = field.split('.')
    result[prop] = state[slice][prop]
  })
  return result
}

/**
 * Form değerlerini memoize etmek için
 * 
 * @param {Object} initialValues - Form başlangıç değerleri
 * @returns {Object} Memoized form values
 * 
 * @example
 * const formValues = useMemoizedFormValues({
 *   name: '',
 *   email: '',
 *   phone: ''
 * })
 */
export const useMemoizedFormValues = (initialValues) => {
  return useMemo(() => initialValues, [])
}

/**
 * Validation schema'ları memoize etmek için
 * 
 * @param {Function} schemaFactory - Schema oluşturan fonksiyon
 * @param {Array} dependencies - Dependency array
 * @returns {Object} Memoized validation schema
 * 
 * @example
 * const validSchema = useMemoizedValidationSchema(() => 
 *   Yup.object().shape({
 *     name: Yup.string().required(intl.formatMessage({id: 'REQUIRED'}))
 *   }), [intl]
 * )
 */
export const useMemoizedValidationSchema = (schemaFactory, dependencies) => {
  return useMemo(schemaFactory, dependencies)
}

/**
 * Event handler'ları toplu olarak memoize etmek için
 * 
 * @param {Object} handlers - Handler fonksiyonları objesi  
 * @param {Object} dependencies - Her handler için dependency map
 * @returns {Object} Memoized handler'lar
 * 
 * @example
 * const handlers = useMemoizedHandlers({
 *   handleSave: (data) => dispatch(saveData(data)),
 *   handleDelete: (id) => dispatch(deleteData(id))
 * }, {
 *   handleSave: [dispatch],
 *   handleDelete: [dispatch]
 * })
 */
export const useMemoizedHandlers = (handlers, dependencies) => {
  const memoizedHandlers = {}
  
  Object.keys(handlers).forEach(key => {
    memoizedHandlers[key] = useCallback(handlers[key], dependencies[key] || [])
  })
  
  return memoizedHandlers
}

/**
 * Tablo kolonlarını memoize etmek için
 * 
 * @param {Array} columnsConfig - Kolon konfigürasyonu
 * @param {Array} dependencies - Dependency array
 * @returns {Array} Memoized columns
 * 
 * @example
 * const columns = useMemoizedColumns([
 *   {
 *     Header: 'Name',
 *     accessor: 'name',
 *     Cell: ({value}) => <span>{value}</span>
 *   }
 * ], [intl, handleSort])
 */
export const useMemoizedColumns = (columnsConfig, dependencies) => {
  return useMemo(() => columnsConfig, dependencies)
}

/**
 * Page URL'i memoize etmek için
 * 
 * @param {Object} location - React Router location object
 * @returns {Array} Memoized page URL segments
 * 
 * @example
 * const pageUrl = useMemoizedPageUrl(location)
 * // ['/admin', 'users', 'create']
 */
export const useMemoizedPageUrl = (location) => {
  return useMemo(() => location.pathname.split('/'), [location.pathname])
}

/**
 * Breadcrumbs'ı memoize etmek için
 * 
 * @param {Function} breadcrumbsFactory - Breadcrumbs oluşturan fonksiyon
 * @param {Array} dependencies - Dependency array
 * @returns {Array} Memoized breadcrumbs
 * 
 * @example
 * const breadcrumbs = useMemoizedBreadcrumbs(() => [
 *   {
 *     title: intl.formatMessage({id: 'USERS'}),
 *     path: '/users',
 *     isActive: false
 *   }
 * ], [intl])
 */
export const useMemoizedBreadcrumbs = (breadcrumbsFactory, dependencies) => {
  return useMemo(breadcrumbsFactory, dependencies)
}

/**
 * Static data listelerini memoize etmek için
 * 
 * @param {Function} dataFactory - Data oluşturan fonksiyon
 * @param {Array} dependencies - Dependency array
 * @returns {any} Memoized static data
 * 
 * @example
 * const buttons = useMemoizedStaticData(() => [
 *   {
 *     title: 'Add User',
 *     onClick: handleAdd,
 *     variant: 'primary'
 *   }
 * ], [handleAdd])
 */
export const useMemoizedStaticData = (dataFactory, dependencies) => {
  return useMemo(dataFactory, dependencies)
}

/**
 * Search ve filter optimizasyonu (genişletilebilir)
 * 
 * @returns {Object} Optimized search/filter handlers
 * 
 * @example
 * const {handleSearch, handleFilter} = useOptimizedSearch()
 */
export const useOptimizedSearch = () => {
  const handleSearch = useCallback((searchTerm) => {
    // Debounced search logic implementasyonu
    console.log('Searching for:', searchTerm)
  }, [])

  const handleFilter = useCallback((filterParams) => {
    console.log('Filtering with:', filterParams)
  }, [])

  return { handleSearch, handleFilter }
}

/**
 * Performance monitoring HOC
 * 
 * @param {React.Component} Component - İzlenecek component
 * @param {string} componentName - Component adı
 * @returns {React.Component} Performance logged component
 * 
 * @example
 * const MonitoredUserList = withPerformanceLogging(UserList, 'UserList')
 */
export const withPerformanceLogging = (Component, componentName) => {
  return function PerformanceLoggedComponent(props) {
    const startTime = performance.now()
    
    const result = Component(props)
    
    const endTime = performance.now()
    if (endTime - startTime > 16) { // 16ms threshold for 60fps
      console.warn(`⚠️ ${componentName} render took ${endTime - startTime}ms`)
    }
    
    return result
  }
}

/**
 * Form hook'u - formlarda sık kullanılan pattern'ler için
 * 
 * @param {Object} config - Form konfigürasyonu
 * @returns {Object} Form utilities
 * 
 * @example
 * const {
 *   formValues,
 *   validationSchema,
 *   handleSubmit
 * } = useOptimizedForm({
 *   initialValues: {name: '', email: ''},
 *   validationRules: {
 *     name: 'required',
 *     email: 'required|email'
 *   },
 *   onSubmit: (values) => dispatch(createUser(values))
 * })
 */
export const useOptimizedForm = ({
  initialValues,
  validationRules,
  onSubmit,
  dependencies = []
}) => {
  const formValues = useMemoizedFormValues(initialValues)
  
  const validationSchema = useMemo(() => {
    // Validation schema oluşturma logic'i
    // Bu kısım Yup ile genişletilebilir
    return validationRules
  }, dependencies)
  
  const handleSubmit = useCallback(onSubmit, dependencies)
  
  return {
    formValues,
    validationSchema,
    handleSubmit
  }
}

/**
 * Kullanım İstatistikleri ve Migration Rehberi:
 * 
 * 🎯 HEDEF: %90 kod tekrarı azaltma
 * 📊 MEVCUT DURUM: 5 komponent optimize edildi
 * 🔄 KALAN İŞ: ~20 komponent daha optimize edilebilir
 * 
 * MIGRATION STRATEJİSİ:
 * 
 * 1. Öncelik sırası:
 *    - ✅ Admin sayfaları (tamamlandı)
 *    - 🔄 Dashboard sayfaları  
 *    - 🔄 List/Table komponentleri
 *    - 🔄 Form komponentleri
 * 
 * 2. Adım adım migration:
 *    - useMemo -> useMemoizedColumns/useMemoizedStaticData
 *    - useCallback -> useMemoizedHandlers
 *    - Manual selectors -> createOptimizedSelector
 * 
 * 3. Test stratejisi:
 *    - React DevTools Profiler ile önce/sonra karşılaştırma
 *    - Performance monitoring ile sürekli izleme
 * 
 * ÖRNEK MIGRATION:
 * 
 * // ❌ Eski hal
 * const columns = useMemo(() => [...], [intl, handler])
 * const buttons = useMemo(() => [...], [])
 * const handleSave = useCallback(() => {...}, [dispatch])
 * 
 * // ✅ Yeni hal  
 * import {useMemoizedColumns, useMemoizedStaticData, useMemoizedHandlers} from '...'
 * 
 * const columns = useMemoizedColumns([...], [intl, handlers.sort])
 * const buttons = useMemoizedStaticData(() => [...], [])
 * const handlers = useMemoizedHandlers({
 *   handleSave: () => {...}
 * }, {
 *   handleSave: [dispatch]
 * })
 */ 